import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatMoney, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Send } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const CURRENCIES = ["usd", "eur", "gbp", "inr", "aed", "sgd"];

export default function SendMoney() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [wallet, setWallet] = useState(null);
  const [email, setEmail] = useState("");
  const [amount, setAmount] = useState("");
  const [currency, setCurrency] = useState("usd");
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/wallet").then(({ data }) => setWallet(data)).catch(() => {});
  }, []);

  const available = wallet?.balances?.[currency] ?? 0;

  const submit = async (e) => {
    e.preventDefault();
    const parsed = parseFloat(amount);
    if (!parsed || parsed <= 0) { toast.error("Enter a valid amount"); return; }
    setLoading(true);
    try {
      await api.post("/wallet/send", { recipient_email: email, amount: parsed, currency, note });
      toast.success(`Sent ${formatMoney(parsed, currency)} to ${email}`);
      await refresh();
      navigate("/transactions");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  return (
    <main data-testid="send-money-page" className="max-w-3xl mx-auto px-6 lg:px-10 py-16">
      <div className="text-xs uppercase tracking-[0.3em] text-gold">P2P transfer</div>
      <h1 className="font-display text-5xl tracking-tighter mt-3">Send.</h1>
      <p className="text-secondary-fg mt-3">Move money between y ℶα members instantly.</p>

      <div className="mt-8 text-sm text-muted-fg">
        Available: <span data-testid="send-available" className="text-gold font-mono">{formatMoney(available, currency)}</span>
      </div>

      <form onSubmit={submit} className="mt-12 space-y-10">
        <div>
          <label className="text-xs uppercase tracking-[0.25em] text-muted-fg">Recipient email</label>
          <input data-testid="send-recipient" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="friend@valiant.io" className="lux-input mt-2" />
        </div>
        <div className="grid grid-cols-12 gap-6">
          <div className="col-span-8">
            <label className="text-xs uppercase tracking-[0.25em] text-muted-fg">Amount</label>
            <input data-testid="send-amount" type="number" min="0.01" step="0.01" required value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" className="lux-input mt-2 font-display text-4xl" />
          </div>
          <div className="col-span-4">
            <label className="text-xs uppercase tracking-[0.25em] text-muted-fg">Currency</label>
            <select data-testid="send-currency" value={currency} onChange={(e) => setCurrency(e.target.value)} className="lux-input mt-2 uppercase">
              {CURRENCIES.map((c) => <option key={c} value={c} className="bg-bg">{c.toUpperCase()}</option>)}
            </select>
          </div>
        </div>
        <div>
          <label className="text-xs uppercase tracking-[0.25em] text-muted-fg">Note (optional)</label>
          <input data-testid="send-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="A toast to good business" className="lux-input mt-2" />
        </div>
        <button data-testid="send-submit" type="submit" disabled={loading} className="btn-gold inline-flex items-center gap-2">
          <Send size={14} /> {loading ? "Sending…" : "Send money"}
        </button>
      </form>
    </main>
  );
}
